/**
 * Step 04 — Leadership & Ambitions
 *
 * Fields (in onboarding sub-step order):
 *   participateInRevolution → leadershipSkillset → politicalExposure
 *   → comfortWithVisibility → socialInfluenceLevel → pastExperience
 *   → desiredPoliticalPosition
 *
 * @module udp-schema/step-04
 */

import { z } from 'zod';

// ─── Main Step 04 Schema ────────────────────────────────────────────────────

export const step04LeadershipSchema = z.object({
  /** Willingness to participate in the revolution of change */
  participateInRevolution: z.enum(['yes_definitely', 'maybe', 'not_sure', 'no']).optional(),

  /** Desired political position — exhaustive enum covering all levels of Indian political office. */
  desiredPoliticalPosition: z
    .enum([
      // Rural local body
      'ward_member',
      'sarpanch',
      'panchayat_samiti_member',
      'block_pramukh',
      'zila_panchayat_member',
      'zila_pramukh',
      // Urban local body
      'municipal_councillor',
      'deputy_mayor',
      'mayor',
      'municipal_chairperson',
      // State
      'mla',
      'mlc',
      'state_minister',
      'deputy_cm',
      'chief_minister',
      'state_speaker',
      'state_opposition_leader',
      // National
      'mp_lok_sabha',
      'mp_rajya_sabha',
      'union_minister',
      'deputy_pm',
      'prime_minister',
      'lok_sabha_speaker',
      'opposition_leader',
      // Party roles
      'booth_worker',
      'mandal_president',
      'district_party_president',
      'state_party_president',
      'national_party_president',
      'party_spokesperson',
      // Constitutional
      'governor',
      'vice_president',
      'president',
      // Meta
      'none',
      'other',
    ])
    .optional(),

  /** Leadership skillset (multi-select string array) */
  leadershipSkillset: z.array(z.string().trim()).optional(),

  /** Level of political exposure */
  politicalExposure: z.enum(['none', 'minimal', 'moderate', 'high', 'very_high']).optional(),

  /** Comfort with public visibility */
  comfortWithVisibility: z
    .enum(['very_comfortable', 'somewhat', 'prefer_behind_scenes', 'not_comfortable'])
    .optional(),

  /** Social influence level */
  socialInfluenceLevel: z
    .enum(['very_influential', 'somewhat', 'average', 'low', 'not_sure'])
    .optional(),

  /** Past political or social experience */
  pastExperience: z
    .enum(['yes_party_work', 'yes_social_work', 'yes_student_politics', 'no_experience', 'other'])
    .optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Step04LeadershipInput = z.infer<typeof step04LeadershipSchema>;
